/**
 * Keyframe animation for DataStorePaneState interpolation.
 *
 * Each keyframe has a fraction [0-1] and a DataStorePaneState value.
 * Between keyframes, linear interpolation (ramp) is performed from the prior
 * keyframe to the following keyframe, restricted to specified attributes.
 *
 * This is the core mechanism for grid/axis cross-fade animations:
 * - Keyframe 0.0:  Source state (visible)
 * - Keyframe 0.49: Source middle (transparent)
 * - Keyframe 0.51: Dest middle (transparent)
 * - Keyframe 1.0:  Dest state (visible)
 *
 * Port of C++ PaneStateKeyframeAnimation from
 * tabanim/main/panestateanimation/PaneStateKeyframeAnimation.cpp
 */

import { Animation } from './BaseAnimation';
import { DataStorePaneState } from './DataStorePaneState';
import { DataStorePaths } from './DataStorePaths';
import { KeyframeFractionUtils } from './KeyframeFractionUtils';
import { PaneStateAttribute } from './PaneStateAttribute';
import { WarpingFunction } from './WarpingFunctionMapUtil';

/**
 * A single keyframe: fraction + pane state + warping function.
 */
export interface PaneStateKeyframe {
  fraction: number;
  state: DataStorePaneState;
  warpingFunction: WarpingFunction | null;
}

export class PaneStateKeyframeAnimation extends Animation {
  private outState: DataStorePaneState;
  private keyframes: PaneStateKeyframe[] = [];
  private restrictToAttributes: PaneStateAttribute[];

  /**
   * @param outState - The shared output state to write interpolated values to
   * @param restrictToAttributes - Only these attributes will be interpolated
   */
  constructor(outState: DataStorePaneState, restrictToAttributes: PaneStateAttribute[]) {
    super();
    this.outState = outState;
    this.restrictToAttributes = restrictToAttributes;
  }

  /**
   * Add a keyframe at the specified fraction.
   * Replaces any existing keyframe at that fraction.
   * Ensures keyframes remain sorted by fraction.
   *
   * C++ Reference: PaneStateKeyframeAnimation::AddKeyframeAtFraction()
   *
   * @param fraction - Fraction [0, 1]
   * @param state - The pane state at this keyframe
   * @param warpingFunction - Easing function for interpolation from this keyframe to the next
   */
  addKeyframeAtFraction(fraction: number, state: DataStorePaneState, warpingFunction: WarpingFunction | null): void {
    fraction = Math.max(0, Math.min(1, fraction));

    let inserted = false;
    for (let i = 0; i < this.keyframes.length; i++) {
      if (this.keyframes[i].fraction === fraction) {
        this.keyframes[i].state = state;
        this.keyframes[i].warpingFunction = warpingFunction;
        inserted = true;
        break;
      } else if (fraction < this.keyframes[i].fraction) {
        this.keyframes.splice(i, 0, { fraction, state, warpingFunction });
        inserted = true;
        break;
      }
    }

    if (!inserted) {
      this.keyframes.push({ fraction, state, warpingFunction });
    }
  }

  /**
   * Update: interpolate between bounding keyframes and write to outState.
   *
   * C++ Reference: PaneStateKeyframeAnimation::Update()
   */
  public update(): void {
    if (this.keyframes.length === 0) {
      return;
    }

    const fraction = this.getFraction();
    const kfIndex = KeyframeFractionUtils.getFractionArrayIndexBeforeOrAt(this.keyframes, fraction);

    if (kfIndex < 0) {
      this.copyAttributesToOutState(this.keyframes[0].state);
      return;
    }

    if (kfIndex >= this.keyframes.length - 1) {
      // At or past last keyframe: copy values from last keyframe
      const lastState = this.keyframes[kfIndex].state;
      this.copyAttributesToOutState(lastState);
    } else {
      // Interpolate between two bounding keyframes
      const kf1 = this.keyframes[kfIndex];
      const kf2 = this.keyframes[kfIndex + 1];

      let segmentFraction = KeyframeFractionUtils.mapFractionToInterval(this.keyframes, fraction, kfIndex, kfIndex + 1);

      // Apply warping function if present
      if (kf1.warpingFunction !== null) {
        segmentFraction = kf1.warpingFunction(segmentFraction);
      }

      // Ramp from kf1.state to kf2.state and write to outState
      kf1.state.rampAttributesInto(kf2.state, segmentFraction, this.restrictToAttributes, this.outState);
    }
  }

  /**
   * Copy attribute values from a keyframe state to the output state.
   * Used when at or past the last keyframe (no interpolation needed).
   */
  private copyAttributesToOutState(sourceState: DataStorePaneState): void {
    for (const attribute of this.restrictToAttributes) {
      const path = DataStorePaths.getKeyPathForAttribute('', attribute);
      if (!path) {
        continue;
      }
      const value = sourceState.getValue(path);
      if (value !== undefined && value !== null) {
        this.outState.setOverride(path, value);
      }
    }
  }

  public getKeyframes(): readonly PaneStateKeyframe[] {
    return this.keyframes;
  }

  public getRestrictToAttributes(): PaneStateAttribute[] {
    return this.restrictToAttributes;
  }

  public getOutState(): DataStorePaneState {
    return this.outState;
  }
}
